import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { GlobalSpinnerService } from 'src/app/shared/services/global-spinner.service';
import { forkJoin, Subscription } from 'rxjs';
import { Utils } from 'src/app/core/services/utils';
import { DocsService } from 'src/app/modules/loan-docs/services/docs.service';
import { NotificationService } from 'src/app/services/notification.service';
import { TaskService } from 'src/app/services/task.service';
import { ApplicationContextBoundComponent } from 'src/app/shared/components/application-context-bound.component';
import Swal from 'sweetalert2';

@Component({
    selector: 'create-escalation-dialog',
    templateUrl: './create-escalation-dialog.component.html',
    styleUrls: ['./create-escalation-dialog.component.scss'],
    standalone: false
})
export class CreateEscalationDialogComponent extends ApplicationContextBoundComponent implements OnInit {

  @ViewChild("escalationForm")
  escalationForm: NgForm;

  loanId: number;

  description: string;

  notes: string;

  uploadedFiles: Array<File> = [];

  autoConvertToPdf: boolean = false;

  useDynamicCompression: boolean = true;

  maxSupportedFileSize: number;

  private _loanInfoChangesSubscription: Subscription;

  constructor(private readonly injector: Injector,
    public activeModal: NgbActiveModal,
    private readonly _taskService: TaskService,
    private readonly _docsService: DocsService,
    private readonly _notifService: NotificationService,
    private readonly _spinner: GlobalSpinnerService) {
    super(injector);
  }

  ngOnInit(): void {
    if (!this.applicationContext?.application?.applicationId) {
      this._loanInfoChangesSubscription = this.applicationContextService.loanInfoChanges.subscribe(({ context }) => {
        this.loanId = context.application?.applicationId;
        this.maxSupportedFileSize = context.globalConfig?.maxSupportedFileSize;
      });
    } else {
      this.loanId = this.applicationContext.application.applicationId;
      this.maxSupportedFileSize = this.applicationContext.globalConfig.maxSupportedFileSize;
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if (this._loanInfoChangesSubscription) {
      this._loanInfoChangesSubscription.unsubscribe();
    }
  }

  onFileUploadChanged = (uploadedFiles: File[]) => {
    let validFiles = uploadedFiles;
    let oversizedFiles = [];
    if (this.maxSupportedFileSize != null) {
      oversizedFiles = uploadedFiles.filter(file => file.size > this.maxSupportedFileSize);
      validFiles = uploadedFiles.filter(file => file.size <= this.maxSupportedFileSize);
    }

    const fileNames = oversizedFiles
      .map(file => `• ${file.name} (${Utils.formatBytes(file.size)})`)
      .join('<br>');

    if (oversizedFiles.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'File(s) Too Large',
        html: `The following file(s) exceed the ${Utils.formatBytes(this.maxSupportedFileSize)} limit and will not be uploaded:<br><br>${fileNames}`,
        confirmButtonText: 'OK',
      }).then(() => {
        this.uploadedFiles = validFiles;
      });
      return;
    }
  }

  onOkClicked = () => {
    if (this.escalationForm) {
      this.escalationForm.form.markAllAsTouched();
      if (this.escalationForm.form.valid) {
        this._spinner.show();
        this._taskService.createEscalation(this.loanId, this.description, this.notes).subscribe((responseTask) => {
          const observables = [];
          if (this.uploadedFiles && this.uploadedFiles.length > 0) {
            this.uploadedFiles.forEach(file => {
              observables.push(this._docsService.uploadFileForTask(file, responseTask.loanDocTaskId, false, this.autoConvertToPdf,
                this.useDynamicCompression));
            });

            forkJoin(observables).subscribe((result) => {
              let errorCount = 0;
              result.forEach(fileUploadResult => {
                if (!fileUploadResult) {
                  errorCount++;
                }
              });
              if (errorCount > 0) {
                console.log("Detected " + errorCount + " error(s).");
              }
              this._spinner.hide();
              this._notifService.showSuccess("Escalation created successfully.", "Success!");
            }, (err) => {
              this._spinner.hide();
              this._notifService.showError('Error: ' + this.getErrorMessage(err), 'File Upload Error');
            });
          }
          this._spinner.hide();
          this._notifService.showSuccess("Escalation created successfully.", "Success!");
        }, (err) => {
          this._spinner.hide();
          this._notifService.showError('Error: ' + this.getErrorMessage(err), 'Escalation creation failed');
        });
        this.activeModal.close();
      }
    }
  }

  onCancelClicked = () => {
    this.activeModal.close('cancel');
  }

  private getErrorMessage = (error: any): string => {
    if (error) {
      if (error.error) {
        if (error.error.message) {
          return error.error.message;
        } else if (error.error.detail) {
          return error.error.detail;
        }
        return error.error;
      } else if (error.message) {
        return error.message;
      }
      return error;
    }
    return "There was an error - please contact your Administrator."
  }
}
